// API Client
import CarbonClient from "../CarbonClient";

// Project versioning: the five flows that need the backend because they touch both
// stores (the Hasura revision functions + the Marten stream pin for materials). Everything else on
// the history page reads Hasura directly. All five are admin-only server-side
// (Permission.DeleteProject).

/**
 * Where a clone / restore gets its Speckle project from — the copy inherits the source's models
 * otherwise, which wires two building projects to the same one (RTLCA-5455). Sent as a string
 * literal: the API deserializes `RestoreSpeckleStreamMode` from the name, so the casing is exact.
 *
 * `Stream` is Speckle's old word for what it now calls a PROJECT (and `Branch` for a MODEL); the
 * names here follow the API's enum, which followed our Hasura column.
 *
 * - `NewStream` (the default, on both ends): a brand-new Speckle project carrying the source's model
 *   NAMES and no geometry — nothing shows in the 3D viewer until a model is pushed to it.
 * - `ExistingStream`: wires the copy to `speckleStreamId`, keeping the models the two have in common
 *   by name. Requires the id; rejected with a 400 when it carries none of them.
 *
 * Both fields are ignored server-side when the source project has no Speckle project at all.
 */
export type SpeckleStreamMode = "NewStream" | "ExistingStream";

/**
 * The Speckle half of the clone / restore body. `speckleStreamId` is a Speckle project id —
 * `project.speckleStreamId` in Hasura, never a building project id — and is omitted for `NewStream`
 * so the body stays exactly what a caller who made no choice would send.
 */
const speckleStreamBody = (speckleStreamMode: SpeckleStreamMode, speckleStreamId?: string) =>
  speckleStreamMode === "ExistingStream" ? { speckleStreamMode, speckleStreamId } : { speckleStreamMode };

/**
 * The new project's id, or a throw. Clone and restore exist to PRODUCE a project, and their callers
 * both announce it and navigate to it — so a 2xx carrying no id is a failure that would otherwise
 * read as success and route to /project/undefined. `data.id` is typed `string`, hence the runtime
 * check: the type describes the contract, not what a proxy or a half-deployed API actually returned.
 */
const newProjectIdFrom = (response: { status: number; data?: { id?: string } }, failure: string): string => {
  if (response.status !== 200 && response.status !== 201) {
    throw new Error(`${failure}: ${response.status}`);
  }
  const id = response.data?.id;
  if (!id) {
    throw new Error(`${failure}: the API returned ${response.status} without a project id`);
  }
  return id;
};

/** Saves the current project state as a named revision (auto-activates). Returns the revision id. */
export const createProjectRevision = async (buildingProjectId: string, name: string): Promise<string> => {
  const response = await CarbonClient.post<{ id: string }>(`api/BuildingProject/${buildingProjectId}/revisions`, { name });
  if (response.status === 200 || response.status === 201) {
    return response.data.id;
  }
  throw new Error(`Failed to save revision: ${response.status}`);
};

/**
 * Saves a trail version as a named revision WITHOUT switching to it: the revision is born frozen —
 * materials pinned at the version, mappings and project information copied from the working
 * state — and the current pointer never moves. Returns the revision id.
 */
export const createProjectRevisionFromVersion = async (
  buildingProjectId: string,
  versionId: string,
  name: string
): Promise<string> => {
  const response = await CarbonClient.post<{ id: string }>(
    `api/BuildingProject/${buildingProjectId}/versions/${versionId}/revisions`,
    { name }
  );
  if (response.status === 200 || response.status === 201) {
    return response.data.id;
  }
  throw new Error(`Failed to save revision from version: ${response.status}`);
};

/** Makes an existing revision the current one for everyone on the project. */
export const activateProjectRevision = async (buildingProjectId: string, revisionId: string): Promise<void> => {
  const response = await CarbonClient.post(`api/BuildingProject/${buildingProjectId}/revisions/${revisionId}/activate`, {});
  if (response.status !== 200) {
    throw new Error(`Failed to activate revision: ${response.status}`);
  }
};

/**
 * Restores a trail version into a brand-new project. Returns the new project id.
 *
 * `speckleStreamMode` picks the new project's Speckle project; see {@link SpeckleStreamMode}.
 */
export const restoreProjectVersion = async (
  buildingProjectId: string,
  versionId: string,
  name: string,
  speckleStreamMode: SpeckleStreamMode = "NewStream",
  speckleStreamId?: string
): Promise<string> => {
  const response = await CarbonClient.post<{ id: string }>(
    `api/BuildingProject/${buildingProjectId}/versions/${versionId}/restore`,
    { name, ...speckleStreamBody(speckleStreamMode, speckleStreamId) }
  );
  return newProjectIdFrom(response, "Failed to restore version");
};

/**
 * The Speckle project restoring this version would connect from: the one the version was recorded
 * against, which may differ from the project's current one. `null` when the version predates the
 * project's Speckle integration — the restored project then gets none, and there is no choice to offer.
 */
export const getVersionSpeckleConnection = async (buildingProjectId: string, versionId: string): Promise<string | null> => {
  const response = await CarbonClient.get<{ speckleStreamId?: string | null }>(
    `api/BuildingProject/${buildingProjectId}/versions/${versionId}/speckle-connection`
  );
  if (response.status !== 200) {
    throw new Error(`Failed to read the version's Speckle connection: ${response.status}`);
  }
  const speckleStreamId = response.data?.speckleStreamId;
  return typeof speckleStreamId === "string" && speckleStreamId.length > 0 ? speckleStreamId : null;
};

/**
 * Clones the project's CURRENT state into a brand-new project. Returns the new project id.
 *
 * The same server-side mechanism as {@link restoreProjectVersion}, pinned at the live head instead
 * of a trail row (the endpoint sends `VersionId: null`), so there is no version id to pass and
 * nothing to pick: materials, project information, transport/waste mappings, custom emission
 * factors, team and the full history all travel, and the source project is untouched. Unlike a
 * version restore it also works on a project whose trail is empty.
 *
 * `speckleStreamMode` picks the clone's Speckle project; see {@link SpeckleStreamMode}.
 */
export const insertProjectClone = async (
  buildingProjectId: string,
  name: string,
  speckleStreamMode: SpeckleStreamMode = "NewStream",
  speckleStreamId?: string
): Promise<string> => {
  const response = await CarbonClient.post<{ id: string }>(`api/BuildingProject/${buildingProjectId}/clone`, {
    name,
    ...speckleStreamBody(speckleStreamMode, speckleStreamId),
  });
  return newProjectIdFrom(response, "Failed to clone project");
};
